import { exportReportPdf, buildReportPdfBlob } from './pdf';
import type { PdfReportOptions } from './pdf';

/**
 * One-stop CSV + PDF export for a page's table. A page declares its columns
 * once (label + how to read the value from a row) and hands the same list to
 * both exporters; the branded PDF comes from lib/pdf's exportReportPdf, the
 * CSV is a UTF-8-BOM, CRLF file Excel opens cleanly. Rows are the page's
 * FULL result set (not the visible page), so pagination never truncates an
 * export.
 */
export interface ExportColumn<T> {
  /** Header text (already localized). */
  label: string;
  /** Raw cell value — CSV always uses this; PDF uses it unless `pdf` is given. */
  value: (row: T) => string | number | null | undefined;
  /** PDF-only presentation (e.g. money formatting); CSV keeps the raw number. */
  pdf?: (row: T) => string | number | null | undefined;
  /** Right-align in the PDF. */
  numeric?: boolean;
}

function csvEscape(v: string | number | null | undefined): string {
  if (v == null) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

export function buildCsv<T>(columns: ExportColumn<T>[], rows: readonly T[]): string {
  const lines: string[] = [columns.map((c) => csvEscape(c.label)).join(',')];
  for (const r of rows) {
    lines.push(columns.map((c) => csvEscape(c.value(r))).join(','));
  }
  // CRLF — Excel on Windows handles it more predictably than bare LF.
  return lines.join('\r\n') + '\r\n';
}

function safeName(name: string): string {
  return name.replace(/[^A-Za-z0-9_-]/g, '_');
}

/** The CSV as a Blob instead of a triggered download — same content
 *  buildCsv would produce, for attaching to an outgoing email (JCD-23)
 *  rather than saving to disk. */
export function buildTableCsvBlob<T>(columns: ExportColumn<T>[], rows: readonly T[]): Blob {
  return new Blob(['﻿' + buildCsv(columns, rows)], { type: 'text/csv;charset=utf-8' });
}

/** Download `<name>.csv` built from the rows; no-op when there is nothing to export. */
export function exportTableCsv<T>(name: string, columns: ExportColumn<T>[], rows: readonly T[]): void {
  if (rows.length === 0) return;
  const blob = buildTableCsvBlob(columns, rows);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = safeName(name) + '.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL?.(url);
}

export interface ExportPdfOptions<T> {
  title: string;
  /** Lines under the title — date range, filters, store. */
  meta?: string[];
  printedBy: string;
  /** Base filename without extension. */
  filename: string;
  columns: ExportColumn<T>[];
  rows: readonly T[];
  orientation?: 'portrait' | 'landscape';
}

function toReportPdfOptions<T>(o: ExportPdfOptions<T>): PdfReportOptions {
  const numericColumns = o.columns.map((c, i) => (c.numeric ? i : -1)).filter((i) => i >= 0);
  return {
    title: o.title,
    meta: o.meta,
    printedBy: o.printedBy,
    filename: safeName(o.filename) + '.pdf',
    columns: o.columns.map((c) => c.label),
    rows: o.rows.map((r) => o.columns.map((c) => {
      const v = (c.pdf ?? c.value)(r);
      return v == null ? '' : v;
    })),
    numericColumns,
    orientation: o.orientation,
  };
}

/** Branded PDF of the rows; no-op when there is nothing to export. */
export function exportTablePdf<T>(o: ExportPdfOptions<T>): void {
  if (o.rows.length === 0) return;
  exportReportPdf(toReportPdfOptions(o));
}

/** The branded PDF as a Blob instead of a triggered download — same content
 *  exportTablePdf would have saved, for attaching to an outgoing email
 *  (JCD-23) rather than saving to disk. */
export function buildTablePdfBlob<T>(o: ExportPdfOptions<T>): Blob {
  return buildReportPdfBlob(toReportPdfOptions(o));
}
